import PortalLayout from "@/components/PortalLayout";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { BarChart3 } from "lucide-react";
import { usePortalCompany } from "@/hooks/usePortalCompany";

const statusLabels: Record<string, string> = {
  not_started: "Not Started",
  in_progress: "In Progress",
  completed: "Completed",
  on_hold: "On Hold",
};

const statusColors: Record<string, string> = {
  not_started: "bg-primary-foreground/20",
  in_progress: "bg-teal",
  completed: "bg-lime",
  on_hold: "bg-yellow-500",
};

const Progress = () => {
  const { companyId } = usePortalCompany();

  const { data: updates = [], isLoading } = useQuery({
    queryKey: ["project-updates", companyId],
    queryFn: async () => {
      const { data } = await supabase
        .from("project_updates")
        .select("*")
        .eq("company_id", companyId!)
        .order("created_at", { ascending: false });
      return data ?? [];
    },
    enabled: !!companyId,
  });

  return (
    <PortalLayout>
      <div className="mb-8">
        <p className="text-teal/60 text-[11px] font-bold tracking-[4px] uppercase font-body mb-2">Projects</p>
        <h1 className="font-display text-3xl text-primary-foreground tracking-tight">
          Progress <span className="accent-text">Updates</span>
        </h1>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-teal/30 border-t-teal rounded-full animate-spin" />
        </div>
      ) : updates.length === 0 ? (
        <div className="glass-card rounded-2xl p-16 text-center">
          <BarChart3 className="w-12 h-12 text-primary-foreground/15 mx-auto mb-4" />
          <p className="text-primary-foreground/40 font-body">No project updates available yet.</p>
          <p className="text-primary-foreground/25 text-sm font-body mt-1">
            Your project manager will post updates here.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {updates.map((update) => (
            <div key={update.id} className="glass-card rounded-2xl p-6">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h3 className="text-lg text-primary-foreground font-display">{update.title}</h3>
                  <p className="text-xs text-primary-foreground/30 font-body mt-1">
                    Updated {new Date(update.updated_at).toLocaleDateString()}
                  </p>
                </div>
                <span className={`px-3 py-1 rounded-full text-[10px] font-bold tracking-[1px] uppercase font-body whitespace-nowrap ${
                  statusColors[update.status ?? "in_progress"]
                } ${update.status === "completed" || update.status === "in_progress" ? "text-navy-deep" : "text-navy-deep"}`}>
                  {statusLabels[update.status ?? "in_progress"]}
                </span>
              </div>

              {/* Progress bar */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-primary-foreground/40 font-body">Progress</span>
                  <span className="text-xs text-teal font-body font-medium">{update.progress_percent}%</span>
                </div>
                <div className="w-full h-2 bg-primary-foreground/5 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-teal to-lime rounded-full transition-all duration-500"
                    style={{ width: `${update.progress_percent}%` }}
                  />
                </div>
              </div>

              {update.content && (
                <p className="text-sm text-primary-foreground/50 font-body leading-relaxed whitespace-pre-wrap">
                  {update.content}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </PortalLayout>
  );
};

export default Progress;
